import React from 'react'
import PropTypes from 'prop-types'
import {Box, Circle, Flex, Text} from '@chakra-ui/react'

import {useTranslation} from 'react-i18next'
import {
  toLocalDate,
  toLocalTime,
  toEpochMillisecond,
} from '../../../utils/formatter'

import {CheckIcon, CrossIcon, ExclamationIcon} from '../../icons'

const ExclamationIconTypes = [
  'became_overdue',
  'customer_payment_reversed',
  'paid_direct',
  'invoice_issue_created',
]

const CrossIconTypes = ['cancelled_cash_in']

const TimelineIcon = ({type}) => {
  if (ExclamationIconTypes.includes(type)) {
    return <ExclamationIcon boxSize={4} />
  }

  if (CrossIconTypes.includes(type)) {
    return <CrossIcon boxSize={4} />
  }

  return <CheckIcon boxSize={5} />
}

TimelineIcon.propTypes = {
  type: PropTypes.string.isRequired,
}

const TimelineNode = ({type, isCurrent}) => (
  <Circle
    size="32px"
    border="1px solid"
    borderColor="mld.neutral.900"
    bg={isCurrent ? 'mld.neutral.900' : 'transparent'}
    color={isCurrent ? 'mld.neutral.0' : 'mld.neutral.900'}
  >
    <TimelineIcon type={type} />
  </Circle>
)

TimelineNode.propTypes = {
  type: PropTypes.string.isRequired,
  isCurrent: PropTypes.bool.isRequired,
}

const InvoiceTimeline = ({timeline}) => {
  const {t} = useTranslation('invoicesPage')

  return (
    <Box
      bg="mld.neutral.100"
      px="sm"
      py="md"
      borderRadius="md"
      className="fs-mask"
    >
      <Text textStyle="headline4" textTransform="uppercase" mb="md">
        {t('invoiceTimeline.heading')}
      </Text>

      {timeline.map((event, index) => {
        const isLast = index + 1 === timeline.length

        return (
          <Flex key={`${event.type}-${event.date}-${index + 1}`} gap="sm">
            <Flex
              direction="column"
              align="flex-end"
              justify="flex-start"
              flexShrink={0}
              width="90px"
              pt="xxs"
            >
              <Text textStyle="body-small" color="mld.neutral.900">
                {toLocalDate(toEpochMillisecond(event.date))}
              </Text>
              {event.date_type === 'datetime' && (
                <Text textStyle="body-small" color="mld.neutral.900">
                  {toLocalTime(toEpochMillisecond(event.date))}
                </Text>
              )}
            </Flex>

            <Flex direction="column" align="center" flexShrink={0}>
              <TimelineNode type={event.type} isCurrent={index === 0} />
              {!isLast && (
                <Box flex="1" width="2px" minH="24px" bg="mld.neutral.500" />
              )}
            </Flex>

            <Box pt="xxs" pb={isLast ? 0 : 'md'} flex="1">
              <Text textStyle="body-copy-medium" color="mld.neutral.900">
                {event.label}
              </Text>
            </Box>
          </Flex>
        )
      })}
    </Box>
  )
}

InvoiceTimeline.defaultProps = {
  timeline: [],
}

InvoiceTimeline.propTypes = {
  timeline: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      date: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
}

export default InvoiceTimeline
